import { Component, Inject, Input, LOCALE_ID, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { OrganisationDto } from '@assetaccountant/api/models/organisation.dto';
import { RegisterDto } from '@assetaccountant/api/models/register.dto';
import { ReportRequestStatus } from '@assetaccountant/api/models/reports/report-request-status';
import { ReportStatusSummary } from '@assetaccountant/api/models/reports/report-status-summary';
import { ReportV4Api } from '@assetaccountant/api/services/report-v4.api';
import { ReportApi } from '@assetaccountant/api/services/report.api';
import { SidePanelComponent } from '@assetaccountant/core/components/side-panel/side-panel.component';
import { ModalService } from '@assetaccountant/core/services/modal.service';
import { getMediumDateFormat, utcToLocalDate } from '@assetaccountant/core/utils/date-utils';
import { downloadBlob } from '@assetaccountant/core/utils/file-utils';
import { TypedNgbModal } from '@assetaccountant/core/utils/typed-modal';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-stored-report-list',
  templateUrl: './stored-report-list.component.html',
  styleUrls: ['./stored-report-list.component.scss'],
  standalone: false,
})
export class StoredReportListComponent implements OnInit, OnDestroy {
  readonly ReportRequestStatus = ReportRequestStatus;

  @Input() organisation?: OrganisationDto;
  @Input() register?: RegisterDto;

  @ViewChild('sidePanel') sidePanel: SidePanelComponent;

  reports?: ReportStatusSummary[] | null = null;

  private reportListSubscription: Subscription | null = null;

  private reportPollingInterval: any | null = null;

  private dateFormat: string;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private modalService: ModalService,
    private reportApi: ReportApi,
    private reportV4Api: ReportV4Api,
    private toastrService: ToastrService,
    @Inject(NgbModal) public ngbModal: TypedNgbModal,
  ) {
    this.dateFormat = getMediumDateFormat(locale);
  }

  // please note that archive is currently disabled - only appears as placeholder for later implementation
  onArchiveReportClick() {
    this.modalService
      .showQuestionModal(
        'Are you sure you want to archive this report? Once archived, you will need to contact technical support to restore it.',
        'Archive Confirmation',
      )
      .subscribe(_ => {
        // archive the report
      });
  }

  getReports() {
    this.reportListSubscription = (
      this.register
        ? this.reportV4Api.listRegisterReports()
        : this.reportApi.listOrganisationReports()
    ).subscribe(listReportStatusResponse => {
      if (this.checkChanged(listReportStatusResponse.reports)) {
        if (this.reports) {
          // assuming this wasn't the first load.
          this.checkToast(listReportStatusResponse.reports);
        }
        this.reports = listReportStatusResponse.reports;

        if (
          this.reports.some(
            r =>
              r.status === ReportRequestStatus.Pending ||
              r.status === ReportRequestStatus.InProgress,
          )
        ) {
          // Fast checking - reports in progress
          clearInterval(this.reportPollingInterval);
          this.reportPollingInterval = setInterval(() => {
            this.getReports();
          }, 5000);
        } else {
          // Slow checking - no reports in progress
          clearInterval(this.reportPollingInterval);
          this.reportPollingInterval = setInterval(() => {
            this.getReports();
          }, 30000);
        }
      }
    });
  }

  ngOnInit() {
    // initialize polling. Ideally should use signalR, but as reports
    // are generated by azure function this would require significant work
    // for such a small piece of functionality.
    this.getReports();
    this.reportPollingInterval = setInterval(() => {
      this.getReports();
    }, 10000);
  }

  ngOnDestroy() {
    // clear polling and subscription
    if (this.reportPollingInterval) {
      clearInterval(this.reportPollingInterval);
    }
    this.reportListSubscription?.unsubscribe();
  }

  reportComplete(report: ReportStatusSummary) {
    return report.status === ReportRequestStatus.Complete;
  }

  downloadReport(report: ReportStatusSummary): void {
    // assuming that the report is complete download it as a file
    if (this.reportComplete(report)) {
      if (this.register) {
        this.reportV4Api
          .downloadReport({
            requestId: report.requestId,
          })
          .subscribe(blob => {
            downloadBlob(blob, report.title, { appendExtension: true });
          });
      } else {
        this.reportApi
          .downloadReport({
            requestId: report.requestId,
          })
          .subscribe(blob => {
            downloadBlob(blob, report.title, { appendExtension: true });
          });
      }
    }
  }

  getReportStatus(status: ReportRequestStatus): string {
    switch (status) {
      case ReportRequestStatus.Pending:
        return 'Pending';
      case ReportRequestStatus.Complete:
        return 'Complete';
      case ReportRequestStatus.InProgress:
        return 'In Progress';
      case ReportRequestStatus.Failed:
        return 'Failed';
      default:
        return 'Unknown';
    }
  }

  showLoading(status: ReportRequestStatus) {
    return status === ReportRequestStatus.Pending || status === ReportRequestStatus.InProgress;
  }

  formatDate(dateString: string) {
    return dateString ? utcToLocalDate(dateString, this.dateFormat) : null;
  }

  // check if anything has changed. This is to prevent redraw of the ui
  // which can cause ui effects when menus open etc.
  checkChanged(newReportStatus: ReportStatusSummary[]): boolean {
    if (this.reports?.length !== newReportStatus.length) return true;

    for (let reportIndex = 0; reportIndex < this.reports.length; reportIndex++) {
      const newReport = newReportStatus[reportIndex];
      const oldReport = this.reports[reportIndex];
      if (oldReport.status !== newReport.status || oldReport.progress !== newReport.progress)
        return true;
    }
    return false;
  }

  // rudamentary toast notification that displays if a report has failed or completed
  checkToast(newReportStatus: ReportStatusSummary[]): void {
    const currentSucceededReports = this.reports?.filter(
      r => r.status === ReportRequestStatus.Complete,
    ).length;
    const currentFailedReports = this.reports?.filter(
      r => r.status === ReportRequestStatus.Failed,
    ).length;

    const newSucceededReports = newReportStatus.filter(
      r => r.status === ReportRequestStatus.Complete,
    ).length;
    const newFailedReports = newReportStatus.filter(
      r => r.status === ReportRequestStatus.Failed,
    ).length;

    if (newFailedReports > (currentFailedReports || 0)) {
      this.toastrService.error('Reports have failed');
    } else if (newSucceededReports > (currentSucceededReports || 0)) {
      this.toastrService.success('Reports have completed');
    }
  }
}
